import React, { useState, useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import DatePicker from "react-multi-date-picker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import fa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import gregorian_en from "react-date-object/locales/gregorian_en";
import "react-multi-date-picker/styles/colors/teal.css";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { memo } from "react";
import backarrow from "../assets/icon/arrow-right.svg";
import {
  FaBook,
  FaBookOpen,
  FaRegListAlt,
  FaDatabase,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaCalculator,
  FaChartLine,
  FaCoins,
  FaPenFancy,
  FaRegFileAlt,
  FaPencilAlt,
} from "react-icons/fa";

const AnimatedBackground = memo(() => {
  const icons = [
    FaBook,
    FaBookOpen,
    FaRegListAlt,
    FaDatabase,
    FaUserGraduate,
    FaChalkboardTeacher,
    FaCalculator,
    FaChartLine,
    FaCoins,
    FaPenFancy,
    FaRegFileAlt,
    FaPencilAlt,
  ];
  const rows = 5,
    cols = 11;
  const iconArray = [];

  for (let i = 0; i < rows; i++)
    for (let j = 0; j < cols; j++) {
      const Icon = icons[Math.floor(Math.random() * icons.length)];
      iconArray.push({
        Icon,
        size: 18 + Math.random() * 20,
        top: (i / rows) * 100 + Math.random() * 5,
        left: (j / cols) * 100 + Math.random() * 5,
        duration: 15 + Math.random() * 10,
        opacity: 0.15 + Math.random() * 0.25,
        xOffset: Math.random() * 10 - 5,
        yOffset: Math.random() * 10 - 5,
      });
    }

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {iconArray.map((item, i) => (
        <motion.div
          key={i}
          className="absolute text-white/50"
          style={{
            top: `${item.top}%`,
            left: `${item.left}%`,
            opacity: item.opacity,
          }}
          initial={{ scale: 0.5 }}
          animate={{
            x: [0, item.xOffset, 0],
            y: [0, item.yOffset, 0],
            scale: [0.5, 1, 0.5],
          }}
          transition={{
            duration: item.duration,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <item.Icon size={item.size} />
        </motion.div>
      ))}
    </div>
  );
});

export default function YearlyFinancialChart() {
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_URL;
  const [selectedDate, setSelectedDate] = useState(
    new DateObject({ calendar: persian, locale: fa })
  );
  const [chartData, setChartData] = useState({
    expectedPayments: 0,
    actualPayments: 0,
  });
  const [noData, setNoData] = useState(false);

  const chartRef = useRef(null);
  const canvasRef = useRef(null);

  const fetchReport = async (dateObj) => {
    setNoData(false);
    if (!dateObj || !dateObj.year) {
      setChartData({ expectedPayments: 0, actualPayments: 0 });
      setNoData(true);
      return;
    }

    const startPersian = new DateObject({
      year: dateObj.year,
      month: 1,
      day: 1,
      calendar: persian,
      locale: fa,
    });
    const startGregorian = startPersian.convert(gregorian, gregorian_en);
    const yearMiladi = startGregorian.year;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_BASE}/api/Financial/Yearly_Report/${yearMiladi}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: ` ${token}`,
          },
        }
      );
      const data = await res.json();

      if (data && data.data) {
        const expected = Number(data.data.expectedPayments) || 0;
        const actual = Number(data.data.actualPayments) || 0;
        setChartData({ expectedPayments: expected, actualPayments: actual });
        setNoData(expected === 0 && actual === 0);
      } else {
        setChartData({ expectedPayments: 0, actualPayments: 0 });
        setNoData(true);
      }
    } catch (err) {
      console.error(err);
      setChartData({ expectedPayments: 0, actualPayments: 0 });
      setNoData(true);
    }
  };

  useEffect(() => {
    fetchReport(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    if (chartRef.current) chartRef.current.destroy();
    if (!canvasRef.current || noData) return;

    const ctx = canvasRef.current.getContext("2d");

    const gradientBlue = ctx.createLinearGradient(0, 0, 0, ctx.canvas.height);
    gradientBlue.addColorStop(0, "#215666");
    gradientBlue.addColorStop(1, "#215662");

    const gradientGreen = ctx.createLinearGradient(0, 0, 0, ctx.canvas.height);
    gradientGreen.addColorStop(0, "#4BC0C0");
    gradientGreen.addColorStop(1, "#9BEAE4");

    const maxValue = Math.max(
      chartData.expectedPayments,
      chartData.actualPayments,
      1
    );
    const minBarHeightPercentage = 40;
    const yValues =
      maxValue > 0
        ? [
            chartData.expectedPayments === 0
              ? 0
              : Math.max(
                  (chartData.expectedPayments / maxValue) * 100,
                  minBarHeightPercentage
                ),
            chartData.actualPayments === 0
              ? 0
              : Math.max(
                  (chartData.actualPayments / maxValue) * 100,
                  minBarHeightPercentage
                ),
          ]
        : [0, 0];

    const maxYScale = Math.max(...yValues) * 1.2;

    chartRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: ["پرداخت احتمالی", "پرداخت شده"],
        datasets: [
          {
            label: "مبلغ",
            data: yValues,
            backgroundColor: [gradientBlue, gradientGreen],
            borderRadius: { topLeft: 20, topRight: 20 },
            barPercentage: 0.45,
            categoryPercentage: 0.9,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "#fff",
            bodyColor: "#fff",
            borderColor: "#36A2EB",
            borderWidth: 1,
            callbacks: {
              label: (context) => {
                const realValue =
                  context.dataIndex === 0
                    ? chartData.expectedPayments
                    : chartData.actualPayments;
                const label = context.chart.data.labels[context.dataIndex];
                return `${label}: ${realValue.toLocaleString("fa-IR")} تومان`;
              },
            },
          },
        },
        scales: {
          y: {
            display: true,
            grid: { display: false },
            ticks: { display: false },
            title: {
              display: true,
              text: "مبلغ (تومان)",
              font: {
                size: 16,
                family: "Inter, Poppins, Roboto, system-ui, sans-serif",
                weight: "bold",
              },
              color: "#333",
              padding: { top: 0, bottom: 10 },
            },
            border: { display: true, color: "#ddd" },
            min: 0,
            max: maxYScale,
          },
          x: {
            grid: { display: false },
            ticks: { display: false },
            border: { display: false },
          },
        },
        animation: { duration: 1000, easing: "easeOutQuart" },
      },
      plugins: [
        {
          id: "valueLabels",
          afterDatasetsDraw: (chart) => {
            const { ctx, chartArea } = chart;
            chart.data.datasets.forEach((dataset, i) => {
              const meta = chart.getDatasetMeta(i);
              meta.data.forEach((bar, index) => {
                const value =
                  index === 0
                    ? chartData.expectedPayments
                    : chartData.actualPayments;
                if (value === 0) return;

                ctx.save();
                ctx.font = "bold 18px sans-serif";
                ctx.textAlign = "center";
                ctx.fillStyle = "#265166";
                ctx.textBaseline = "bottom";

                let labelY = bar.y - 5;
                if (labelY < chartArea.top + 15) labelY = chartArea.top + 15;

                ctx.fillText(value.toLocaleString("fa-IR"), bar.x, labelY);
                ctx.restore();
              });
            });
          },
        },
        {
          id: "categoryLabels",
          afterDatasetsDraw: (chart) => {
            const { ctx, chartArea } = chart;
            if (!chartArea) return;

            chart.data.labels.forEach((label, index) => {
              const bar = chart.getDatasetMeta(0).data[index];
              if (!bar) return;

              ctx.save();
              ctx.font = "bold 18px sans-serif";
              ctx.textAlign = "center";
              ctx.fillStyle = "#333";
              ctx.textBaseline = "top";
              ctx.fillText(label, bar.x, chartArea.bottom + 10);
              ctx.restore();
            });
          },
        },
      ],
    });
  }, [chartData, noData]);

  const currentPersianYear = selectedDate?.year
    ? `${selectedDate.year}`
    : "سال نامشخص";

  if (!localStorage.getItem("token")) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="relative min-h-screen w-full bg-gray-800 flex items-center justify-center p-4">
      <AnimatedBackground />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="mt-11 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl p-6 w-full max-w-xl mx-auto border border-white/30"
      >
        {/* هدر */}
        <div className="flex items-center justify-between mb-6">
          <img
            src={backarrow}
            alt="back"
            className="w-8 h-8 cursor-pointer hover:scale-110 transition"
            onClick={() => navigate("/Mainpage/FinancialBalance")}
          />
          <h2 className="text-2xl font-bold text-gray-800">
            گزارش مالی سالیانه
          </h2>
          <div className="w-8" />
        </div>

        {/* عنوان سال */}
        <p className="text-center text-lg font-semibold text-[#265166] mb-6">
          سال {currentPersianYear}
        </p>

        {/* انتخاب سال */}
        <div className="flex justify-center mb-6">
          <div className="w-full max-w-xs">
            <DatePicker
              value={selectedDate}
              onChange={(date) => {
                if (date?.year) {
                  setSelectedDate(
                    new DateObject({
                      year: date.year,
                      month: 1,
                      day: 1,
                      calendar: persian,
                      locale: fa,
                    })
                  );
                }
              }}
              calendar={persian}
              locale={fa}
              format="YYYY"
              onlyYearPicker
              calendarPosition="bottom-center"
              className="teal"
              placeholder="انتخاب سال"
              inputClassName="w-full px-5 py-3.5 text-center text-base font-bold text-[#265166] bg-white border-4 border-[#1c3073] rounded-xl focus:outline-none cursor-pointer"
            />
          </div>
        </div>

        {/* لجند */}
        <div className="flex justify-center gap-8 mb-8 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-[#215666] rounded" />
            <span className="text-gray-700 font-medium">پرداخت احتمالی</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-[#4BC0C0] rounded" />
            <span className="text-gray-700 font-medium">پرداخت شده</span>
          </div>
        </div>

        {/* چارت */}
        <div className="h-80">
          {noData ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-4xl mb-4">
                📊
              </div>
              <h3 className="text-lg font-bold text-gray-600 mb-2">
                داده‌ای یافت نشد!
              </h3>
              <p className="text-gray-500 text-sm px-4">
                {currentPersianYear === "سال نامشخص"
                  ? "لطفاً سال را انتخاب کنید."
                  : `برای سال ${currentPersianYear} اطلاعاتی موجود نیست.`}
              </p>
            </div>
          ) : (
            <canvas ref={canvasRef} className="w-full h-full" />
          )}
        </div>
      </motion.div>
    </div>
  );
}
